import { Injectable } from '@angular/core';
import { SignupDraft } from './signup';

const DRAFT_STORAGE_KEY = 'pp.signup.draft';

/**
 * Keeps the half-filled sign-up form across a full page navigation.
 *
 * **Why this exists at all.** The funnel submits by calling `authenticate()`, and with Keycloak that
 * redirects the whole document away and back. Without somewhere to put it, a five-step form the
 * customer has just spent ten minutes on is gone by the time they return — and the step they would
 * land on is the one that sent them away, so they would not even know what had been lost.
 *
 * **`sessionStorage`, not `localStorage`.** The draft carries a legal name, a registered address, a
 * VAT ID and an administrator's email: personal and commercial data, on a device that may be shared.
 * `sessionStorage` is scoped to the browser tab and cleared when it closes, which is as long as the
 * Keycloak round trip needs and no longer. The remembered organization key is the one thing that
 * does outlive the session, and it lives in `localStorage` deliberately — see
 * {@link RememberedOrgKeyStorage} for why one key is a different proposition from a whole form.
 *
 * **Its own helper, because there is not one.** `@processpuzzle/util` has no storage abstraction
 * (verified), so this library brings a small one rather than scattering `sessionStorage` calls
 * through the store.
 *
 * Every method swallows its failures. Storage throws in more situations than is commonly assumed —
 * Safari's private mode, a browser with storage disabled, a quota that is full — and none of them is
 * a reason to break a form that works perfectly well without persistence. The cost of failure is a
 * draft that does not survive the round trip, which is exactly the behaviour before this class
 * existed.
 */
@Injectable({ providedIn: 'root' })
export class SignupDraftStorage {
  private get storage(): Storage | undefined {
    try {
      return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
    } catch {
      return undefined;
    }
  }

  read(): SignupDraft | undefined {
    try {
      const stored = this.storage?.getItem(DRAFT_STORAGE_KEY);
      if (!stored) return undefined;
      const parsed = JSON.parse(stored) as unknown;
      // A non-object is a corrupt or foreign value under our key; treat it as no draft rather than
      // letting it reach a form that would then read properties off a string.
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as SignupDraft) : undefined;
    } catch {
      return undefined;
    }
  }

  write(draft: SignupDraft): void {
    try {
      this.storage?.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch {
      // See the class comment: persistence is a convenience, and losing it is not a failure of the
      // form.
    }
  }

  /**
   * Called once a sign-up has been accepted.
   *
   * Not merely tidiness: a submitted draft left in storage would repopulate the funnel the next time
   * the customer opened it, inviting a second sign-up with the same organization key — which answers
   * 409 and looks like the platform having lost their first one.
   */
  clear(): void {
    try {
      this.storage?.removeItem(DRAFT_STORAGE_KEY);
    } catch {
      // As above.
    }
  }
}
